import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import { SourceEpochTracker, evaluateMediaHealth, type EpochReason, type MediaHealth, type PlayerPhase } from '@urbanrats/domain';

export type PlayerSource =
  | { kind: 'file' | 'hls'; sourceId: string; url: string; maxSourceAgeMs: number | null; loop: boolean }
  | { kind: 'blob'; sourceId: string; file: File; loop: boolean }
  | { kind: 'webcam'; sourceId: string };

export type PlayerError = 'NETWORK' | 'DECODE' | 'UNSUPPORTED_OR_BLOCKED' | 'ABORTED' | 'PERMISSION_DENIED' | 'NO_DEVICE' | 'DEVICE_ERROR' | 'STREAM_LOST';

export interface PlayerStatus {
  phase: PlayerPhase;
  error: PlayerError | null;
  errorDetail: string | null;
  epoch: string;
  epochReason: EpochReason;
  epochCount: number;
  /** Reasons of every epoch so far, oldest first (evidence of discontinuities). */
  epochHistory: EpochReason[];
  width: number;
  height: number;
  measuredFps: number | null;
  /** HLS: age relative to the segment publish time (not capture time). null when unknown. */
  mediaAgeMs: number | null;
  reconnecting: boolean;
  health: MediaHealth;
}

export interface FrameInfo {
  sourceEpoch: string;
  ptsMicros: number;
}

export interface PlayerControls {
  seek(seconds: number): void;
  restart(): void;
  currentTime(): number;
}

interface Props {
  source: PlayerSource;
  onStatus: (s: PlayerStatus) => void;
  onFrame: (f: FrameInfo) => void;
  controlsRef?: Ref<PlayerControls>;
  label: string;
}

type HlsInstance = import('hls.js').default;

/**
 * Video element wrapper. Emits frame-accurate presentation timestamps (requestVideoFrameCallback) for
 * overlays, maps media failures to explicit error states, and starts a new source epoch on every
 * timeline discontinuity (seek, loop, restart, reconnect, HLS discontinuity). There is no timer-driven
 * fallback: if no frames arrive, the picture is reported frozen.
 */
export function MediaPlayer({ source, onStatus, onFrame, controlsRef, label }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cb = useRef({ onStatus, onFrame });
  useEffect(() => {
    cb.current = { onStatus, onFrame };
  }, [onStatus, onFrame]);

  const state = useRef({
    tracker: new SourceEpochTracker(source.sourceId),
    epochReason: 'LOAD' as EpochReason,
    phase: 'loading' as PlayerPhase,
    error: null as PlayerError | null,
    errorDetail: null as string | null,
    lastFrameAt: null as number | null,
    bufferingSince: null as number | null,
    lastPts: -1,
    frames: [] as number[],
    reconnecting: false,
    mediaAgeMs: null as number | null,
    pendingSeekReason: null as EpochReason | null,
    pendingSeekUntil: 0,
    reconnectEpochDone: false,
  });

  useImperativeHandle(controlsRef, () => ({
    seek: (s) => {
      const v = videoRef.current;
      if (!v) return;
      state.current.pendingSeekReason = 'SEEK';
      v.currentTime = Math.max(0, s);
    },
    restart: () => {
      const v = videoRef.current;
      if (!v) return;
      state.current.pendingSeekReason = 'RESTART';
      v.currentTime = 0;
      void v.play().catch(() => undefined);
    },
    currentTime: () => videoRef.current?.currentTime ?? 0,
  }));

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const st = state.current;
    st.tracker = new SourceEpochTracker(source.sourceId);
    st.epochReason = 'LOAD';
    st.phase = 'loading';
    st.error = null;
    st.errorDetail = null;
    st.lastFrameAt = null;
    st.lastPts = -1;
    st.frames = [];
    let disposed = false;
    let hls: HlsInstance | null = null;
    let stream: MediaStream | null = null;
    let objectUrl: string | null = null;
    let rvfc = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retries = 0;

    const emit = () => {
      if (disposed) return;
      const now = performance.now();
      const fpsWindow = st.frames.filter((t) => now - t <= 1000);
      st.frames = fpsWindow;
      const health = evaluateMediaHealth({
        phase: st.phase,
        nowMs: now,
        lastFrameAtMs: st.lastFrameAt,
        bufferingSinceMs: st.bufferingSince,
        mediaAgeMs: st.mediaAgeMs,
        maxSourceAgeMs: source.kind === 'file' || source.kind === 'hls' ? source.maxSourceAgeMs : null,
      });
      cb.current.onStatus({
        phase: st.phase,
        error: st.error,
        errorDetail: st.errorDetail,
        epoch: st.tracker.current(),
        epochReason: st.epochReason,
        epochCount: st.tracker.history.length,
        epochHistory: st.tracker.history.map((h) => h.reason),
        width: v.videoWidth,
        height: v.videoHeight,
        measuredFps: st.lastFrameAt === null ? null : fpsWindow.length,
        mediaAgeMs: st.mediaAgeMs,
        reconnecting: st.reconnecting,
        health,
      });
    };
    const setPhase = (p: PlayerPhase) => {
      if (st.phase === 'error' && p !== 'loading') return;
      if (p === 'buffering' && st.phase !== 'buffering') st.bufferingSince = performance.now();
      if (p !== 'buffering') st.bufferingSince = null;
      st.phase = p;
      emit();
    };
    const fail = (e: PlayerError, detail: string | null) => {
      st.error = e;
      st.errorDetail = detail;
      st.phase = 'error';
      emit();
    };
    const bump = (reason: EpochReason) => {
      st.tracker.bump(reason);
      st.epochReason = reason;
      st.lastPts = -1;
      emit();
    };

    const onFrameCb = (_now: number, meta: VideoFrameCallbackMetadata) => {
      if (disposed) return;
      const t = performance.now();
      st.lastFrameAt = t;
      st.frames.push(t);
      const pts = Math.round(meta.mediaTime * 1_000_000);
      st.lastPts = pts;
      if (hls?.playingDate) st.mediaAgeMs = Math.max(0, Date.now() - hls.playingDate.getTime());
      cb.current.onFrame({ sourceEpoch: st.tracker.current(), ptsMicros: pts });
      rvfc = v.requestVideoFrameCallback(onFrameCb);
    };
    rvfc = v.requestVideoFrameCallback(onFrameCb);

    const onSeeking = () => {
      // A loop shows up as a seek back to 0 right after the end of the file.
      const near0 = v.currentTime < 0.5;
      const nearEnd = Number.isFinite(v.duration) && st.lastPts / 1e6 > v.duration - 0.6;
      const pending = st.pendingSeekReason && (st.pendingSeekUntil === 0 || performance.now() < st.pendingSeekUntil) ? st.pendingSeekReason : null;
      // Initial positioning (e.g. HLS live edge) before the first frame belongs to the LOAD epoch.
      if (st.lastPts === -1 && st.tracker.history.length === 1 && !pending) return;
      // Live HLS has no user seeking: a jump not requested by us is the stream skipping ahead (reconnect).
      const liveJump = source.kind === 'hls' && !pending;
      const reason: EpochReason = st.reconnecting || liveJump ? 'RECONNECT' : (pending ?? (v.loop && near0 && nearEnd ? 'LOOP' : 'SEEK'));
      if (st.reconnecting) st.reconnectEpochDone = true;
      st.pendingSeekReason = null;
      st.pendingSeekUntil = 0;
      bump(reason);
    };
    const onError = () => {
      const err = v.error;
      if (!err) return;
      const map: Record<number, PlayerError> = { 1: 'ABORTED', 2: 'NETWORK', 3: 'DECODE', 4: 'UNSUPPORTED_OR_BLOCKED' };
      fail(map[err.code] ?? 'DECODE', err.message || null);
    };
    const listeners: [string, () => void][] = [
      ['loadedmetadata', emit],
      ['playing', () => setPhase('playing')],
      ['waiting', () => setPhase('buffering')],
      ['stalled', () => st.phase === 'playing' && emit()],
      ['pause', () => !v.ended && st.phase !== 'error' && setPhase('paused')],
      ['ended', () => setPhase('ended')],
      ['seeking', onSeeking],
      ['error', onError],
    ];
    for (const [ev, fn] of listeners) v.addEventListener(ev, fn);
    const healthTimer = setInterval(emit, 500);

    const start = async () => {
      v.muted = true;
      if (source.kind === 'webcam') {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          if (disposed) return;
          v.srcObject = stream;
          stream.getVideoTracks()[0]?.addEventListener('ended', () => fail('STREAM_LOST', 'camera track ended'));
        } catch (e) {
          const name = e instanceof DOMException ? e.name : '';
          fail(name === 'NotAllowedError' || name === 'SecurityError' ? 'PERMISSION_DENIED' : name === 'NotFoundError' || name === 'OverconstrainedError' ? 'NO_DEVICE' : 'DEVICE_ERROR', e instanceof Error ? e.message : null);
          return;
        }
      } else if (source.kind === 'blob') {
        objectUrl = URL.createObjectURL(source.file);
        v.loop = source.loop;
        v.src = objectUrl;
      } else if (source.kind === 'file') {
        v.crossOrigin = 'anonymous';
        v.loop = source.loop;
        v.src = source.url;
      } else {
        const { default: Hls } = await import('hls.js');
        if (disposed) return;
        if (!Hls.isSupported()) {
          v.src = source.url;
        } else {
          hls = new Hls({ liveSyncDurationCount: 2, manifestLoadingMaxRetry: 2, fragLoadingMaxRetry: 2, levelLoadingMaxRetry: 2 });
          let lastCc: number | null = null;
          hls.on(Hls.Events.FRAG_CHANGED, (_e, d) => {
            if (lastCc !== null && d.frag.cc !== lastCc) bump('RESTART'); // stream discontinuity
            lastCc = d.frag.cc;
          });
          hls.on(Hls.Events.FRAG_LOADED, () => {
            if (st.reconnecting) {
              st.reconnecting = false;
              retries = 0;
              // One new epoch per reconnect (unless the live-edge jump already created it).
              if (!st.reconnectEpochDone) bump('RECONNECT');
              st.reconnectEpochDone = false;
              st.pendingSeekReason = 'RECONNECT';
              st.pendingSeekUntil = performance.now() + 3000;
            }
          });
          hls.on(Hls.Events.ERROR, (_e, d) => {
            if (!d.fatal) return;
            if (d.type === Hls.ErrorTypes.NETWORK_ERROR && retries < 8) {
              st.reconnecting = true;
              setPhase('buffering');
              retries++;
              retryTimer = setTimeout(() => hls?.startLoad(), 1500);
            } else if (d.type === Hls.ErrorTypes.MEDIA_ERROR && retries < 2) {
              retries++;
              hls?.recoverMediaError();
            } else {
              fail(d.type === Hls.ErrorTypes.NETWORK_ERROR ? 'NETWORK' : 'DECODE', d.details);
            }
          });
          hls.loadSource(source.url);
          hls.attachMedia(v);
        }
      }
      try {
        await v.play();
      } catch (e) {
        if (!disposed && st.phase !== 'error' && !(e instanceof DOMException && e.name === 'AbortError')) setPhase('paused');
      }
    };
    void start();

    return () => {
      disposed = true;
      clearInterval(healthTimer);
      clearTimeout(retryTimer);
      v.cancelVideoFrameCallback(rvfc);
      for (const [ev, fn] of listeners) v.removeEventListener(ev, fn);
      hls?.destroy();
      stream?.getTracks().forEach((t) => t.stop());
      v.removeAttribute('src');
      v.srcObject = null;
      v.load();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source]);

  return <video ref={videoRef} className="media" playsInline muted aria-label={label} data-testid="player" />;
}
